import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTypedTranslation } from '@app/hooks';
import {
    handleGetSyncAgentData,
    handleGetSyncAgentAttributeMapData,
    handleGetProvisioningPoliciesDTOData,
    setExceptionHandler,
    addDetailsBreadcrumb,
    getModalSwitch,
    removeDetailsBreadcrumb
} from '@app/store/actions';
import { passlyTimeDuration, passlyHumanizeDate, toBoolean, booleanStringToYesNo, createTranslation, getLockoutString } from '@app/helpers';
import EditSyncAgentDialog from '@app/pages/directoryManager/dialogs/editSyncAgent.dialog';
import { IProvisioningPolicyDTO, IAgentDetail } from '@app/models';
import { OuTree, IModalData, ModalComponent } from '@app/components';
import EditAUSIAgentDialog from '@app/pages/directoryManager/dialogs/editAUSISyncAgent.dialog';
import { syncService } from '@app/services';
import { AxiosResponse } from 'axios';
import { isEmpty } from 'lodash';

interface IProps {
    match: {
        params: {
            id: string;
        };
    };
}

const DIRSYNC_2_TYPE = 'DirSync 2.0';

const SyncDetailsPage = (props: IProps) => {
    const provisioningPoliciesDTO: IProvisioningPolicyDTO[] = useSelector((state: any) => state.provisioningPolicies.provisioningPoliciesDTO);
    const dirsyncAgent: IAgentDetail = useSelector((state: any) => state.syncs.syncAgent);
    const [dirSyncAUSIAgent, setDirSyncAUSIAgent] = useState<any>({});
    const syncAgentAttributeMap: any = useSelector((state: any) => state.syncs.syncAgentAttributeMap);

    const [directoryPolicy, setDirectoryPolicy] = useState<any>({});

    const dispatch = useDispatch();

    const { translation } = useTypedTranslation();

    const dirsyncAgentId: string = props.match.params.id;

    useEffect(() => {
        dispatch(setExceptionHandler(true));
        dispatch(handleGetSyncAgentData(dirsyncAgentId));
        dispatch(handleGetProvisioningPoliciesDTOData());

        return () => {
            dispatch(removeDetailsBreadcrumb());
        };
    }, []);

    const getAUSISyncAgent = () => {
        syncService.getAUSISyncAgent(dirsyncAgentId).then((result: AxiosResponse) => {
            setDirSyncAUSIAgent(result.data);
        });
    };

    useEffect(() => {
        if (!isEmpty(dirsyncAgent) && dirsyncAgent.AgentType === DIRSYNC_2_TYPE) {
            getAUSISyncAgent();
        } else if (!isEmpty(dirsyncAgent)) {
            dispatch(addDetailsBreadcrumb(dirsyncAgent.AgentName));
            dispatch(handleGetSyncAgentAttributeMapData(dirsyncAgentId));

            if (dirsyncAgent?.AgentName !== undefined) {
                setDirectoryPolicy(JSON.parse(dirsyncAgent.RawDirectoryPolicy));
            }
        }

    }, [dirsyncAgent]);
    useEffect(() => {
        dispatch(addDetailsBreadcrumb(dirSyncAUSIAgent.agentName));
    }, [dirSyncAUSIAgent]);

    const modalSwitchState: boolean = useSelector((state: any) => state.modal);
    const [modalData, setModalData] = useState<IModalData>({
        headerTitle: '',
        modalSwitcher: modalSwitchState
    });

    const getAssignedProvisioningPolicyName = (): string => {
        let name: string = translation('UNKNOWN_POLICY');

        for (let policy of provisioningPoliciesDTO) {
            if (policy.Id === dirsyncAgent.ProvisioningPolicyId) {
                name = policy.DisplayName;
            }
        }
        return name;
    };

    const editAgent = () => {
        dispatch(getModalSwitch(true));
        let modalData = {
            headerTitle: translation('EDIT_USER'),
            modalSwitcher: modalSwitchState,
            form: (
                <EditSyncAgentDialog
                    agent={dirsyncAgent}
                    editAgentCompleteCallback={onEditAgentComplete}
                    provisioningPolicies={provisioningPoliciesDTO}
                />
            )
        };

        if (!isEmpty(dirSyncAUSIAgent)) {
            modalData = {
                headerTitle: translation('EDIT_AUSI_AGENT_TITLE'),
                modalSwitcher: modalSwitchState,
                form: (
                    <EditAUSIAgentDialog
                        agent={dirSyncAUSIAgent}
                        onCompleteCallback={onEditAUSIAgentComplete}
                    />
                )
            };
        }

        setModalData(modalData);
    };

    const onEditAgentComplete = (shouldReloadUser: boolean) => {
        dispatch(getModalSwitch(false));
        if (shouldReloadUser) {
            dispatch(handleGetSyncAgentData(dirsyncAgentId));
        }
    };
    const onEditAUSIAgentComplete = () => {
        dispatch(getModalSwitch(false));
        getAUSISyncAgent();
    };

    const parseAgentAttributes = (obj: any) => {
        let parsedObj = JSON.parse(obj);
        return Object.keys(parsedObj).map(function (key) {
            if (parsedObj[key]) {
                return {
                    name: key,
                    value: parsedObj[key]
                };
            } else {
                return {
                    name: key,
                    value: 'Empty'
                };
            }
        });
    };

    let getPolicyTime = (policyName: string, policyValue: string): string => {
        if (!policyValue) {
            return '';
        }

        let duration: number = passlyTimeDuration(policyValue);

        if (policyName === 'capability.MaximumAge' && duration <= 0) {
            return translation('NO_MAXIMUM');
        }

        return passlyHumanizeDate(policyValue);
    };

    let getPolicyCharacters = (policyValue: string): any => {
        if (!policyValue || policyValue === '') {
            return null;
        }

        let valInt = parseInt(policyValue);

        if (valInt === 1) {
            return valInt + ' ' + translation('CHAR_LEN_SINGLE');
        } else {
            return valInt + ' ' + translation('CHAR_LEN_PLURAL');
        }
    };

    return (
        <>
            <ModalComponent {...modalData} />
            <div className='content--narrow detailPage'>
                <div className='detailPage__section'>
                    <div className='detailPage__section-head'>{translation('AGENT_DETAILS_TITLE')}</div>

                    {dirsyncAgent && dirsyncAgent.AgentName !== undefined && (
                        <div className='detailPage__section-content'>
                            <ul className='detailPage__list'>
                                <li>
                                    <a className='detailPage__list--primary-label' onClick={() => editAgent()}>
                                        {translation('EDIT')}
                                    </a>
                                </li>
                                <li>
                                    <span className='detailPage__list--primary-label'>{translation('AGENT_NAME')}</span>
                                    <span className='detailPage__list--secondary-label'>{dirsyncAgent.AgentName}</span>
                                </li>
                                <li>
                                    <span className='detailPage__list--primary-label'>{translation('AGENT_STATE')}</span>
                                    <span className='detailPage__list--secondary-label'>
                                        {/*TODO: it should be fixed on the backend one day*/}
                                        {dirsyncAgent.ProvisioningState === 'Disabled' ? translation('PROVISION_STATE_INACTIVE') : dirsyncAgent.ProvisioningState}
                                    </span>
                                </li>
                                <li>
                                    <span className='detailPage__list--primary-label'>{translation('AGENT_VERSION')}</span>
                                    <span className='detailPage__list--secondary-label'>{dirsyncAgent.Version}</span>
                                </li>
                                <li>
                                    <span className='detailPage__list--primary-label'>{translation('PROVISIONING_POLICY')}</span>
                                    {provisioningPoliciesDTO.length > 0 ? (
                                        <span className='detailPage__list--secondary-label'>{getAssignedProvisioningPolicyName()}</span>
                                    ) : (
                                        <span className='detailPage__list--secondary-label'>{translation('LOADING_POLICIES')}</span>
                                    )}
                                </li>
                                <li>
                                    <span className='detailPage__list--primary-label'>{translation('SYNC_SUPPORT_KERBEROS')}</span>
                                    <span className='detailPage__list--secondary-label'>
                                        {translation(createTranslation(booleanStringToYesNo(directoryPolicy['policy.KerberosEnabled'])))}
                                    </span>
                                </li>

                                <h3 className='detailPage__list--primary-label'>{translation('SYNC_PASSWORD_POLICY')}</h3>
                                <li>
                                    <span className='detailPage__list--primary-label'>{translation('LOCK_AFTER_ATTEMPTS')}</span>
                                    <span className='detailPage__list--secondary-label'>
                                        {getLockoutString(directoryPolicy['capability.LockAfterAttempts'], translation)}
                                    </span>
                                </li>
                                {directoryPolicy['capability.LockAfterAttempts'] !== '0' && (
                                    <li>
                                        <span className='detailPage__list--primary-label'>{translation('LOCKOUT_DURATION')}</span>
                                        <span className='detailPage__list--secondary-label'>
                                            {getPolicyTime('capability.LockoutDuration', directoryPolicy['capability.LockoutDuration'])}
                                        </span>
                                    </li>
                                )}
                                {toBoolean(directoryPolicy['capability.CanChangePassword']) && (
                                    <>
                                        <li>
                                            <span className='detailPage__list--primary-label'>{translation('ENABLE_PASSWORD_WRITEBACK')}</span>
                                            <span className='detailPage__list--secondary-label'>
                                                {translation(
                                                    createTranslation(booleanStringToYesNo(directoryPolicy['policy.ChangePasswordEnabled']))
                                                )}
                                            </span>
                                        </li>
                                    </>
                                )}

                                {toBoolean(directoryPolicy['policy.ChangePasswordEnabled']) && (
                                    <>
                                        <li>
                                            <span className='detailPage__list--primary-label'>{translation('PASS_REQUIRES_COMPLEX')}</span>
                                            <span className='detailPage__list--secondary-label'>
                                                {translation(
                                                    createTranslation(booleanStringToYesNo(directoryPolicy['capability.RequiresComplexity']))
                                                )}
                                            </span>
                                        </li>
                                        <li>
                                            <span className='detailPage__list--primary-label'>{translation('AD_PASS_MIN_AGE')}</span>
                                            <span className='detailPage__list--secondary-label'>
                                                {getPolicyTime('capability.MinimumAge', directoryPolicy['capability.MinimumAge'])}
                                            </span>
                                        </li>
                                        <li>
                                            <span className='detailPage__list--primary-label'>{translation('AD_PASS_MAX_AGE')}</span>
                                            <span className='detailPage__list--secondary-label'>
                                                {getPolicyTime('capability.MaximumAge', directoryPolicy['capability.MaximumAge'])}
                                            </span>
                                        </li>
                                        <li>
                                            <span className='detailPage__list--primary-label'>{translation('AD_PASS_MIN_LENGTH')}</span>
                                            <span className='detailPage__list--secondary-label'>
                                                {getPolicyCharacters(directoryPolicy['capability.MinimumLength'])}
                                            </span>
                                        </li>
                                    </>
                                )}
                            </ul>
                        </div>
                    )}
                </div>

                {!isEmpty(dirsyncAgent) && dirsyncAgent.AgentType !== DIRSYNC_2_TYPE ? (
                    <div className='detailPage__section'>
                        <div className='detailPage__section-head'>{translation('AGENT_OUS')}</div>
                        <div className='detailPage__section-description'>
                            <div className='detailPage__section-description-row'>
                                <div className='ouTree__checkbox ouTree__checkbox-indeterminate' />
                                <div dangerouslySetInnerHTML={{ __html: translation('OU_TREE_DESCRIPTION_GREEN') }} />
                            </div>
                            <div className='detailPage__section-description-row'>
                                <div className='ouTree__checkbox ouTree__checkbox-part-of-children' />
                                <div dangerouslySetInnerHTML={{ __html: translation('OU_TREE_DESCRIPTION_LIGHT_BLUE') }} />
                            </div>
                            <div className='detailPage__section-description-row'>
                                <div className='ouTree__checkbox ouTree__checkbox-checked' />
                                <div dangerouslySetInnerHTML={{ __html: translation('OU_TREE_DESCRIPTION_BLUE') }} />
                            </div>
                        </div>
                        <div className='detailPage__section-content'>
                            <OuTree agentId={dirsyncAgentId} />
                        </div>
                    </div>
                ) : null}

                {isEmpty(dirsyncAgent) && dirsyncAgent.AgentType !== DIRSYNC_2_TYPE ? (
                    <div className='detailPage__section'>
                    <div className='detailPage__section-head'>{translation('AGENT_ATTRIBUTES')}</div>

                    {syncAgentAttributeMap && syncAgentAttributeMap.User !== undefined && (
                        <div className='detailPage__section-content'>
                            <ul className='detailPage__list'>
                                <h3 className='detailPage__list--primary-label'>{translation('AGENT_ATTRIBUTES_INFORMATION_USER_REQ')}</h3>
                                {parseAgentAttributes(syncAgentAttributeMap.User.Required).map((item: any, index: number) => (
                                    <li key={index}>
                                        <span className='detailPage__list--primary-label'>{item.name}</span>
                                        <span className='detailPage__list--secondary-label'>{item.value}</span>
                                    </li>
                                ))}
                                <h3 className='detailPage__list--primary-label'>{translation('AGENT_ATTRIBUTES_INFORMATION_USER_CUSTOM')}</h3>
                                {parseAgentAttributes(syncAgentAttributeMap.User.Custom).map((item: any, index: number) => (
                                    <li key={index}>
                                        <span className='detailPage__list--primary-label'>{item.name}</span>
                                        <span className='detailPage__list--secondary-label'>{item.value}</span>
                                    </li>
                                ))}
                                <h3 className='detailPage__list--primary-label'>{translation('AGENT_ATTRIBUTES_INFORMATION_GROUP_REQ')}</h3>
                                {parseAgentAttributes(syncAgentAttributeMap.Group.Required).map((item: any, index: number) => (
                                    <li key={index}>
                                        <span className='detailPage__list--primary-label'>{item.name}</span>
                                        <span className='detailPage__list--secondary-label'>{item.value}</span>
                                    </li>
                                ))}
                                <h3 className='detailPage__list--primary-label'>{translation('AGENT_ATTRIBUTES_INFORMATION_GROUP_CUSTOM')}</h3>
                                {parseAgentAttributes(syncAgentAttributeMap.Group.Custom).map((item: any, index: number) => (
                                    <li key={index}>
                                        <span className='detailPage__list--primary-label'>{item.name}</span>
                                        <span className='detailPage__list--secondary-label'>{item.value}</span>
                                    </li>
                                ))}
                                <h3 className='detailPage__list--primary-label'>{translation('AGENT_ATTRIBUTES_INFORMATION_COMPUTER_REQ')}</h3>
                                {parseAgentAttributes(syncAgentAttributeMap.Computer.Required).map((item: any, index: number) => (
                                    <li key={index}>
                                        <span className='detailPage__list--primary-label'>{item.name}</span>
                                        <span className='detailPage__list--secondary-label'>{item.value}</span>
                                    </li>
                                ))}
                                <h3 className='detailPage__list--primary-label'>{translation('AGENT_ATTRIBUTES_INFORMATION_COMPUTER_CUSTOM')}</h3>
                                {parseAgentAttributes(syncAgentAttributeMap.Computer.Custom).map((item: any, index: number) => (
                                    <li key={index}>
                                        <span className='detailPage__list--primary-label'>{item.name}</span>
                                        <span className='detailPage__list--secondary-label'>{item.value}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
                ) : null}
            </div>
        </>
    );
};

export default SyncDetailsPage;
